import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { MENU_DATA } from "../../Models/ArrowData";

const Header = () => {
  const [isHovered, setIsHovered] = useState(false); // 데스크탑용
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // 모바일용
  const [activeMobileMenu, setActiveMobileMenu] = useState(null); // 모바일 서브메뉴 토글

  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  // 경로 이동 시 모바일 메뉴 닫기
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveMobileMenu(null);
  }, [location]);

  const handleNavigation = (menu, objects) => {
    if (menu?.newPage && objects === "sub") {
      window.open(menu.path, "_blank");
      return;
    } else if (menu === "/") {
      navigate("/");
      return;
    }
    navigate(menu.path);
  };

  const toggleMobileSubMenu = (index) => {
    setActiveMobileMenu(activeMobileMenu === index ? null : index);
  };

  return (
    <HeaderContainer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      isHovered={isHovered}
      isHome={isHome}
      isMobileMenuOpen={isMobileMenuOpen}
    >
      <InnerWrapper>
        <Logo
          isHovered={isHovered || isMobileMenuOpen}
          isHome={isHome}
          onClick={() => handleNavigation("/")}
        />

        {/* 데스크탑 메뉴 */}
        <Nav className="desktop-nav">
          <MenuList>
            {MENU_DATA.map((menu, index) => (
              <MenuItem key={index} isHovered={isHovered} isHome={isHome}>
                <span
                  className="main-title"
                  onClick={() => handleNavigation(menu, "title")}
                >
                  {menu.title}
                </span>
                <SubMenu isHovered={isHovered}>
                  {menu.subMenus.map((sub, subIdx) => (
                    <li
                      key={subIdx}
                      onClick={() => handleNavigation(sub, "sub")}
                    >
                      {sub.title}
                    </li>
                  ))}
                </SubMenu>
              </MenuItem>
            ))}
          </MenuList>
        </Nav>

        {/* 햄버거 버튼 (모바일 전용) */}
        <Hamburger
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          isOpen={isMobileMenuOpen}
          isHome={isHome}
          isHovered={isHovered}
        >
          <div />
          <div />
          <div />
        </Hamburger>
      </InnerWrapper>

      {/* 모바일 사이드 메뉴 */}
      <MobileMenuOverlay
        isOpen={isMobileMenuOpen}
        onClick={() => setIsMobileMenuOpen(false)}
      />
      <MobileMenuSide isOpen={isMobileMenuOpen}>
        <MobileMenuHeader>MENU</MobileMenuHeader>
        <MobileMenuList>
          {MENU_DATA.map((menu, index) => (
            <MobileMenuItem key={index}>
              <div
                className="mobile-main-title"
                onClick={() => toggleMobileSubMenu(index)}
              >
                {menu.title}
                <span>{activeMobileMenu === index ? "−" : "+"}</span>
              </div>
              <MobileSubList isOpen={activeMobileMenu === index}>
                {menu.subMenus.map((sub, subIdx) => (
                  <li key={subIdx} onClick={() => handleNavigation(sub, "sub")}>
                    {sub.title}
                  </li>
                ))}
              </MobileSubList>
            </MobileMenuItem>
          ))}
        </MobileMenuList>
      </MobileMenuSide>

      <DropdownBackground className="dropdown-bg" isHovered={isHovered} />
    </HeaderContainer>
  );
};

export default Header;

/* --- Styles --- */

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 80px;
  z-index: 9999;
  background-color: ${(props) =>
    !props.isHome || props.isHovered || props.isMobileMenuOpen
      ? "#ffffff"
      : "transparent"};
  box-shadow: ${(props) =>
    !props.isHome || props.isHovered
      ? "0 4px 20px rgba(0, 0, 0, 0.08)"
      : "none"};
  transition: all 0.4s ease;

  @media (max-width: 768px) {
    height: 80px;
  }
`;

const InnerWrapper = styled.div`
  width: 90%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 100;
`;

const Logo = styled.div`
  width: 200px;
  height: 45px;
  cursor: pointer;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: left center;
  background-image: url(${(props) =>
    !props.isHome || props.isHovered
      ? "/images/Nav/DHK_CI.png"
      : "/images/Nav/MENUDHKS.png"});

  @media (max-width: 768px) {
    width: 150px;
  }
`;

const Nav = styled.nav`
  height: 100%;
  @media (max-width: 1000px) {
    display: none; /* 모바일에서는 숨김 */
  }
`;

const MenuList = styled.ul`
  display: flex;
  height: 100%;
  gap: 100px;
  list-style: none;
`;

const MenuItem = styled.li`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  .main-title {
    font-size: 1.05rem;
    font-weight: 600;
    color: ${(props) => (!props.isHome || props.isHovered ? "#333" : "#fff")};
    cursor: pointer;
  }
`;

const SubMenu = styled.ul`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%)
    ${(props) => (props.isHovered ? "translateY(0)" : "translateY(-10px)")};
  opacity: ${(props) => (props.isHovered ? "1" : "0")};
  visibility: ${(props) => (props.isHovered ? "visible" : "hidden")};
  min-width: 180px;
  background: transparent;
  padding: 20px 0;
  list-style: none;
  li {
    padding: 10px;
    font-size: 0.9rem;
    color: #555;
    text-align: center;
    cursor: pointer;
    &:hover {
      color: #00d2ff;
    }
  }
`;

/* --- 햄버거 버튼 --- */
const Hamburger = styled.div`
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 28px;
  height: 20px;
  cursor: pointer;

  @media (max-width: 1000px) {
    display: flex;
  }

  div {
    width: 100%;
    height: 2px;
    background-color: ${(props) =>
      props.isOpen || !props.isHome || props.isHovered ? "#333" : "#fff"};
    transition: all 0.3s ease;

    &:nth-child(1) {
      transform: ${(props) =>
        props.isOpen ? "rotate(45deg) translateY(6.5px)" : "none"};
    }
    &:nth-child(2) {
      opacity: ${(props) => (props.isOpen ? "0" : "1")};
    }
    &:nth-child(3) {
      transform: ${(props) =>
        props.isOpen ? "rotate(-45deg) translateY(-6.5px)" : "none"};
    }
  }
`;

/* --- 모바일 사이드바 --- */
const MobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.isOpen ? "block" : "none")};
  z-index: 999;
`;

const MobileMenuSide = styled.div`
  position: fixed;
  top: 0;
  right: ${(props) => (props.isOpen ? "0" : "-100%")};
  width: 80%;
  max-width: 300px;
  height: 100%;
  background: #fff;
  z-index: 1000;
  transition: right 0.4s ease;
  padding: 80px 20px;
  display: flex;
  flex-direction: column;
`;

const MobileMenuHeader = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  margin-bottom: 30px;
  color: #333;
  border-bottom: 2px solid #00d2ff;
  padding-bottom: 10px;
`;

const MobileMenuList = styled.ul`
  list-style: none;
  padding: 0;
`;

const MobileMenuItem = styled.li`
  border-bottom: 1px solid #eee;
  .mobile-main-title {
    display: flex;
    justify-content: space-between;
    padding: 15px 0;
    font-size: 1.1rem;
    font-weight: 700;
    color: #333;
    cursor: pointer;
  }
`;

const MobileSubList = styled.ul`
  list-style: none;
  padding: 0;
  max-height: ${(props) => (props.isOpen ? "500px" : "0")};
  overflow: hidden;
  transition: all 0.3s ease;
  background: #f9f9f9;

  li {
    padding: 12px 20px;
    font-size: 0.95rem;
    color: #666;
    border-bottom: 1px solid #f0f0f0;
    &:last-child {
      border: none;
    }
  }
`;

const DropdownBackground = styled.div`
  position: absolute;
  top: 80px;
  left: 0;
  width: 100%;
  background-color: #fff;
  height: ${(props) => (props.isHovered ? "350px" : "0")};
  opacity: ${(props) => (props.isHovered ? "1" : "0")};
  visibility: ${(props) => (props.isHovered ? "visible" : "hidden")};
  transition: all 0.4s ease;
  @media (max-width: 1000px) {
    display: none;
  }
`;
